import React, { useEffect, useState } from "react";
import { Table, Switch, Popconfirm, Tag } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import SuperAdminContainer from "../SuperAdminContainer";
import ScreenHeader from "../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import logger from "../../../utils/logger";
import {
  fbGetGroupsForMilestoneReview,
  fbSetReferralVerified,
  fbClearReferral,
  fbSetMilestone2Verified,
} from "../../../services/firebaseService/endPoints/admin/referrals";

const REQUIRED_VERIFIED_COUNT = 2;

const getMilestone1Status = (referrals) => {
  const verified = Object.values(referrals).filter((r) => r.verifiedBySuperAdmin);
  return verified.length >= REQUIRED_VERIFIED_COUNT ? "COMPLETE" : "IN PROGRESS";
};

const ReferralsReview = () => {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await fbGetGroupsForMilestoneReview();
      setTableData(data);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const withPending = async (key, action) => {
    setPendingKey(key);
    try {
      await action();
      await fetchData();
    } catch (error) {
      logger.error(error);
    } finally {
      setPendingKey(null);
    }
  };

  const columns = [
    {
      title: "Group",
      dataIndex: "groupName",
      key: "groupName",
      className: "header-white",
    },
    {
      title: "Milestone 1 — Refer",
      key: "milestone1",
      className: "header-white",
      render: (data) => {
        const referrals = data.milestones?.milestone1?.referrals || {};
        const filledEntries = Object.entries(referrals).filter(([, r]) => r.confirmed);

        if (!filledEntries.length) return <span>—</span>;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Tag
              color={getMilestone1Status(referrals) === "COMPLETE" ? "green" : "orange"}
              style={{ width: "fit-content" }}
            >
              {getMilestone1Status(referrals)}
            </Tag>
            {filledEntries.map(([index, referral]) => {
              const key = `${data.id}-referral-${index}`;
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ minWidth: 120 }}>{referral.name}</span>
                  <Switch
                    size="small"
                    checked={referral.verifiedBySuperAdmin}
                    loading={pendingKey === key}
                    onChange={(checked) =>
                      withPending(key, () => fbSetReferralVerified(data.id, index, checked))
                    }
                  />
                  <Popconfirm
                    title="Clear this referral?"
                    description="The group admin will need to re-enter it."
                    onConfirm={() => withPending(key, () => fbClearReferral(data.id, index))}
                  >
                    <DeleteOutlined style={{ cursor: "pointer", color: "#999" }} />
                  </Popconfirm>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Milestone 2 — Amplify the Brand",
      key: "milestone2",
      className: "header-white",
      render: (data) => {
        const milestone2 = data.milestones?.milestone2 || {};
        if (!milestone2.announcement) return <span>—</span>;

        const key = `${data.id}-milestone2`;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <a href={milestone2.announcement} target="_blank" rel="noreferrer">
              {milestone2.announcement}
            </a>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                size="small"
                checked={milestone2.verifiedBySuperAdmin}
                loading={pendingKey === key}
                onChange={(checked) =>
                  withPending(key, () => fbSetMilestone2Verified(data.id, checked))
                }
              />
              <span>Verified</span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <SuperAdminContainer>
      <ScreenHeader
        title="Referrals Review"
        elementCount={tableData?.length}
        actions={<ChantButton onClick={fetchData}>Refresh</ChantButton>}
      />
      <Table
        bordered
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          position: ["bottomLeft"],
        }}
      />
    </SuperAdminContainer>
  );
};

export default ReferralsReview;
